import React, { useEffect, useMemo, useState } from "react";
import api from "../../services/apiClient.ts";


const ActiveGroupsTable = () => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [orderBy, setOrderBy] = useState("created_at");
  const [orderDir, setOrderDir] = useState("desc");
  const [meta, setMeta] = useState({ total: 0, last_page: 1 });
  const TENANT = "default-tenant-1";

  const load = async (opts = {}) => {
    setLoading(true);
    setErr("");
    try {
      const res = await api.get("/v1/reports/group-aggregate", {
        params: {
          only_active: 1,
          per_page: perPage,
          page: opts.page ?? page,
          q: q || undefined,
          order_by: orderBy,
          order_dir: orderDir,
        },
        headers: { "X-Tenant": TENANT },
      });
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setRows(data);
      setMeta({
        total: res.data?.total ?? data.length,
        last_page: res.data?.last_page ?? 1,
      });
    } catch (e) {
      console.error(e);
      setErr("تعذر جلب البيانات. تحقق من الاتصال والصلاحيات.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load({ page: 1 }); /* أول مرة */ }, [orderBy, orderDir]);
  const refresh = () => load({ page: 1 });

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.received += Number(r.total_received || 0);
        acc.remaining += Number(r.total_remaining || 0);
        return acc;
      },
      { received: 0, remaining: 0 }
    );
  }, [rows]);

  const fmt = (v) =>
    Number(v || 0).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }) + ' د.ع';

  return (
    <div className="p-4">
      <h2 className="text-lg font-semibold mb-3">
        المجموعات النشطة (أقساط غير مكتملة)
      </h2>

      <div className="flex gap-2 mb-3 items-center">
        <input
          placeholder="بحث باسم المجموعة…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="border rounded px-3 py-2"
        />
        <button
          className="px-3 py-2 bg-blue-600 text-white rounded"
          onClick={() => refresh()}
          disabled={loading}
        >
          {loading ? "جارٍ البحث…" : "بحث / تحديث"}
        </button>

        <select
          className="border rounded px-2 py-2"
          value={orderBy}
          onChange={(e) => setOrderBy(e.target.value)}
          title="ترتيب حسب"
        >
          <option value="created_at">تاريخ الإنشاء</option>
          <option value="name">اسم المجموعة</option>
          <option value="received">إجمالي المستلم</option>
          <option value="remaining">إجمالي المتبقي</option>
        </select>
        <select
          className="border rounded px-2 py-2"
          value={orderDir}
          onChange={(e) => setOrderDir(e.target.value)}
          title="اتجاه الترتيب"
        >
          <option value="desc">تنازلي</option>
          <option value="asc">تصاعدي</option>
        </select>
      </div>

      {err && <div className="bg-red-50 text-red-700 p-3 mb-3 rounded">{err}</div>}

      <div className="app-table-wrap">
        <table className="app-table min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 text-right">#</th>
              <th className="p-2 text-right">اسم المجموعة</th>
              <th className="p-2 text-right">إجمالي المستلم</th>
              <th className="p-2 text-right">إجمالي المتبقي</th>
              <th className="p-2 text-right">تاريخ الإنشاء</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading ? (
              <tr>
                <td colSpan={5} className="p-4 text-center text-gray-600">
                  لا توجد بيانات.
                </td>
              </tr>
            ) : (
              rows.map((r, idx) => (
                <tr key={r.id}>
                  <td className="p-2 text-right">{idx + 1}</td>
                  <td className="p-2 text-right">{r.group_name}</td>
                  <td className="p-2 text-right">{fmt(r.total_received)}</td>
                  <td className="p-2 text-right">{fmt(r.total_remaining)}</td>
                  <td className="p-2 text-right">
                    {r.created_at
                      ? new Date(r.created_at).toLocaleDateString()
                      : "-"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-gray-50">
              <tr>
                <td className="p-2 text-right font-semibold">---</td>
                <td className="p-2 text-right font-semibold">المجاميع</td>
                <td className="p-2 text-right font-semibold">
                  {fmt(totals.received)}
                </td>
                <td className="p-2 text-right font-semibold">
                  {fmt(totals.remaining)}
                </td>
                <td className="p-2 text-right font-semibold">---</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* تنقل بسيط للصفحات (إذا احتجته لاحقًا) */}
      {/* <div className="mt-3 flex items-center gap-2">
        <button
          className="px-3 py-1 border rounded"
          onClick={() => { setPage((p) => Math.max(1, p - 1)); load({ page: page - 1 }); }}
          disabled={loading || page <= 1}
        >
          السابق
        </button>
        <span>صفحة {page} من {meta.last_page}</span>
        <button
          className="px-3 py-1 border rounded"
          onClick={() => { setPage((p) => p + 1); load({ page: page + 1 }); }}
          disabled={loading || page >= meta.last_page}
        >
          التالي
        </button>
      </div> */}
    </div>
  );
};

export default ActiveGroupsTable;
